/**
 * React Hooks for Vesant KYC SDK
 *
 * Provides convenient React hooks for using the KYC service in Next.js applications.
 */

import { useState, useEffect, useCallback, useRef, createElement } from 'react';
import { createRoot } from "react-dom/client"
import FaceCaptureModal from "./FaceCaptureModal"
import { KycClient } from './client';
import type {
  KycRequest,
  KycRequestFilters,
  DocumentVerificationRequest,
  DocumentVerificationResponse,
  KycStatus,
  KycAlert,
  KycAlertFilters,
  UpdateKycAlertRequest,
  KycOverview,
  KycPreferences,
  UpdateKycPreferencesRequest,
  PaginationParams,
  UseKycSubmissionOptions,
  UseKycSubmissionResult,
  UseKycRequestsOptions,
  UseKycRequestsResult,
  UseKycAlertsOptions,
  UseKycAlertsResult,
  UseKycOverviewOptions,
  UseKycOverviewResult,
  UseKycPreferencesResult,
  CheckKycStatusResponse,
} from './types';

// ============================================================================
// Shared Hooks for Auto-fetch and Polling
// ============================================================================

/** Hook for auto-fetching on mount */
function useAutoFetch(autoFetch: boolean | undefined, fetchFn: () => void): void {
  useEffect(() => {
    if (autoFetch) {
      fetchFn();
    }
  }, [autoFetch, fetchFn]);
}

/** Hook for setting up polling intervals */
function usePolling(pollInterval: number | undefined, fetchFn: () => void): void {
  const pollIntervalRef = useRef<NodeJS.Timeout>();

  useEffect(() => {
    if (pollInterval && pollInterval > 0) {
      pollIntervalRef.current = setInterval(fetchFn, pollInterval);
      return () => {
        if (pollIntervalRef.current) {
          clearInterval(pollIntervalRef.current);
        }
      };
    }
    return undefined;
  }, [pollInterval, fetchFn]);
}

// ============================================================================
// useKycSubmission Hook
// ============================================================================

/**
 * React hook for submitting KYC document verification
 *
 * @param client - KycClient instance
 * @param options - Hook options
 * @returns KYC submission state and methods
 *
 * @example
 * ```tsx
 * function KycForm() {
 *   const { submit, submission, loading, error, reset } = useKycSubmission(client, {
 *     onComplete: (result) => {
 *       console.log("Verification submitted:", result.reference);
 *     },
 *     onError: (err) => {
 *       console.error("Submission failed:", err.message);
 *     }
 *   });
 *
 *   const handleSubmit = async (formData: FormData) => {
 *     await submit({
 *       reference: formData.get("customerId"),
 *       email: formData.get("email"),
 *       document: {
 *         proof: formData.get("documentImage"),
 *         selected_type: ["id_card", "passport"]
 *       }
 *     });
 *   };
 *
 *   return (
 *     <form onSubmit={handleSubmit}>
 *       {loading && <Spinner />}
 *       {error && <ErrorMessage>{error.message}</ErrorMessage>}
 *       {submission && <SuccessMessage>Submitted: {submission.reference}</SuccessMessage>}
 *     </form>
 *   );
 * }
 * ```
 */
export function useKycSubmission(
  client: KycClient,
  options: UseKycSubmissionOptions = {}
): UseKycSubmissionResult {
  const [submission, setSubmission] = useState<DocumentVerificationResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const submit = useCallback(
    async (request: DocumentVerificationRequest): Promise<DocumentVerificationResponse> => {
      setLoading(true);
      setError(null);

      try {
        const result = await client.submitVerification(request);
        setSubmission(result);
        options.onComplete?.(result);
        return result;
      } catch (err) {
        const error = err instanceof Error ? err : new Error('Verification submission failed');
        setError(error);
        options.onError?.(error);
        throw error;
      } finally {
        setLoading(false);
      }
    },
    [client, options]
  );

  const reset = useCallback(() => {
    setSubmission(null);
    setError(null);
    setLoading(false);
  }, []);

  return {
    submit,
    submission,
    loading,
    error,
    reset,
  };
}

// ============================================================================
// useKycRequests Hook
// ============================================================================

/**
 * React hook for managing KYC requests
 *
 * @param client - KycClient instance
 * @param options - Hook options
 * @returns KYC requests state and management methods
 *
 * @example
 * ```tsx
 * function KycDashboard() {
 *   const {
 *     requests,
 *     total,
 *     loading,
 *     fetchRequests,
 *     updateStatus
 *   } = useKycRequests(client, {
 *     autoFetch: true,
 *     filters: { status: "pending" },
 *     pagination: { page: 1, page_size: 20 }
 *   });
 *
 *   return (
 *     <div>
 *       <h2>{total} Pending KYC Requests</h2>
 *       {requests.map(req => (
 *         <KycCard
 *           key={req.id}
 *           request={req}
 *           onApprove={() => updateStatus(req.reference, "accepted", "Verified")}
 *           onReject={() => updateStatus(req.reference, "declined", "Invalid document")}
 *         />
 *       ))}
 *     </div>
 *   );
 * }
 * ```
 */
export function useKycRequests(
  client: KycClient,
  options: UseKycRequestsOptions = {}
): UseKycRequestsResult {
  const [requests, setRequests] = useState<KycRequest[]>([]);
  const [pagination, setPagination] = useState<{ total: number; page: number; limit: number; totalPages: number } | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchRequests = useCallback(
    async (filters?: KycRequestFilters, paginationParams?: PaginationParams) => {
      setLoading(true);
      setError(null);

      try {
        const result = await client.listKycRequests(
          filters || options.filters,
          paginationParams || options.pagination
        );
        setRequests(result.data);
        setPagination(result.pagination);
      } catch (err) {
        const error = err instanceof Error ? err : new Error('Failed to fetch KYC requests');
        setError(error);
        throw error;
      } finally {
        setLoading(false);
      }
    },
    [client, options.filters, options.pagination]
  );

  const getRequest = useCallback(
    async (id: string): Promise<KycRequest> => {
      try {
        return await client.getKycRequest(id);
      } catch (err) {
        const error = err instanceof Error ? err : new Error('Failed to get KYC request');
        setError(error);
        throw error;
      }
    },
    [client]
  );

  const updateStatus = useCallback(
    async (reference: string, status: KycStatus, reason?: string) => {
      try {
        await client.updateKycStatus({ reference, status, reason });
        setRequests((prev) =>
          prev.map((req) =>
            req.reference === reference
              ? {
                ...req,
                status,
                ...(status === 'accepted'
                  ? { accepted_reason: reason }
                  : { declined_reason: reason }),
              }
              : req
          )
        );
      } catch (err) {
        const error = err instanceof Error ? err : new Error('Failed to update KYC status');
        setError(error);
        throw error;
      }
    },
    [client]
  );

  const refresh = useCallback(async () => {
    await fetchRequests();
  }, [fetchRequests]);

  useAutoFetch(options.autoFetch, fetchRequests);
  usePolling(options.pollInterval, fetchRequests);

  return {
    requests,
    pagination,
    loading,
    error,
    fetchRequests,
    getRequest,
    updateStatus,
    refresh,
  };
}

// ============================================================================
// useKycAlerts Hook
// ============================================================================

/**
 * React hook for managing KYC alerts
 *
 * @param client - KycClient instance
 * @param options - Hook options
 * @returns KYC alerts state and management methods
 *
 * @example
 * ```tsx
 * function AlertsPanel() {
 *   const {
 *     alerts,
 *     total,
 *     loading,
 *     fetchAlerts,
 *     updateAlert
 *   } = useKycAlerts(client, {
 *     autoFetch: true,
 *     filters: { status: ["pending", "in_progress"], risk: "critical" },
 *     pollInterval: 30000
 *   });
 *
 *   return (
 *     <div>
 *       <h2>{total} Critical Alerts</h2>
 *       {alerts.map(alert => (
 *         <AlertCard
 *           key={alert.id}
 *           alert={alert}
 *           onAssign={(userId) => updateAlert(alert.id, { assigned_for: userId, status: "in_progress" })}
 *           onResolve={() => updateAlert(alert.id, { status: "resolved", reason: "Verified" })}
 *         />
 *       ))}
 *     </div>
 *   );
 * }
 * ```
 */
export function useKycAlerts(
  client: KycClient,
  options: UseKycAlertsOptions = {}
): UseKycAlertsResult {
  const [alerts, setAlerts] = useState<KycAlert[]>([]);
  const [pagination, setPagination] = useState<{ total: number; page: number; limit: number; totalPages: number } | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchAlerts = useCallback(
    async (filters?: KycAlertFilters, paginationParams?: PaginationParams) => {
      setLoading(true);
      setError(null);

      try {
        const result = await client.listAlerts(
          filters || options.filters,
          paginationParams || options.pagination
        );
        setAlerts(result.alerts);
        setPagination(result.pagination);
      } catch (err) {
        const error = err instanceof Error ? err : new Error('Failed to fetch KYC alerts');
        setError(error);
        throw error;
      } finally {
        setLoading(false);
      }
    },
    [client, options.filters, options.pagination]
  );

  const getAlert = useCallback(
    async (id: string): Promise<KycAlert> => {
      try {
        return await client.getAlert(id);
      } catch (err) {
        const error = err instanceof Error ? err : new Error('Failed to get KYC alert');
        setError(error);
        throw error;
      }
    },
    [client]
  );

  const updateAlert = useCallback(
    async (id: string, update: UpdateKycAlertRequest) => {
      try {
        await client.updateAlert(id, update);
        setAlerts((prev) =>
          prev.map((alert) =>
            alert.id === id ? { ...alert, ...update } : alert
          )
        );
      } catch (err) {
        const error = err instanceof Error ? err : new Error('Failed to update KYC alert');
        setError(error);
        throw error;
      }
    },
    [client]
  );

  const refresh = useCallback(async () => {
    await fetchAlerts();
  }, [fetchAlerts]);

  useAutoFetch(options.autoFetch, fetchAlerts);
  usePolling(options.pollInterval, fetchAlerts);

  return {
    alerts,
    pagination,
    loading,
    error,
    fetchAlerts,
    getAlert,
    updateAlert,
    refresh,
  };
}

// ============================================================================
// useKycOverview Hook
// ============================================================================

/**
 * React hook for KYC overview statistics
 *
 * @param client - KycClient instance
 * @param options - Hook options
 * @returns KYC overview state
 *
 * @example
 * ```tsx
 * function KycStats() {
 *   const { overview, loading, refresh } = useKycOverview(client, {
 *     autoFetch: true,
 *     fromDate: "2024-01-01T00:00:00Z",
 *     toDate: "2024-12-31T23:59:59Z"
 *   });
 *
 *   if (loading) return <Spinner />;
 *
 *   return (
 *     <div>
 *       <StatCard label="Total" value={overview?.total} />
 *       <StatCard label="Pending" value={overview?.pending} />
 *       <StatCard label="Approved" value={overview?.approved} />
 *       <StatCard label="Rejected" value={overview?.rejected} />
 *       <StatCard label="High Risk" value={overview?.high_risk} />
 *     </div>
 *   );
 * }
 * ```
 */
export function useKycOverview(
  client: KycClient,
  options: UseKycOverviewOptions = {}
): UseKycOverviewResult {
  const [overview, setOverview] = useState<KycOverview | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchOverview = useCallback(
    async (fromDate?: string, toDate?: string) => {
      setLoading(true);
      setError(null);

      try {
        const result = await client.getOverview(
          fromDate || options.fromDate,
          toDate || options.toDate
        );
        setOverview(result);
      } catch (err) {
        const error = err instanceof Error ? err : new Error('Failed to fetch KYC overview');
        setError(error);
        throw error;
      } finally {
        setLoading(false);
      }
    },
    [client, options.fromDate, options.toDate]
  );

  useAutoFetch(options.autoFetch, fetchOverview);

  return {
    overview,
    loading,
    error,
    refresh: fetchOverview,
  };
}

// ============================================================================
// useKycPreferences Hook
// ============================================================================

/**
 * React hook for managing KYC preferences
 *
 * @param client - KycClient instance
 * @param autoFetch - Auto-fetch preferences on mount (default: true)
 * @returns KYC preferences state and methods
 *
 * @example
 * ```tsx
 * function PreferencesForm() {
 *   const { preferences, loading, updatePreferences, refresh } = useKycPreferences(client);
 *
 *   const handleUpdate = async (e: FormEvent) => {
 *     e.preventDefault();
 *     await updatePreferences({
 *       is_face_verification_required: true,
 *       required_document_count: 2
 *     });
 *   };
 *
 *   if (loading) return <Spinner />;
 *
 *   return (
 *     <form onSubmit={handleUpdate}>
 *       <Checkbox
 *         label="Require Face Verification"
 *         checked={preferences?.is_face_verification_required}
 *       />
 *       <NumberInput
 *         label="Required Documents"
 *         value={preferences?.required_document_count}
 *       />
 *       <Button type="submit">Save</Button>
 *     </form>
 *   );
 * }
 * ```
 */
export function useKycPreferences(
  client: KycClient,
  autoFetch: boolean = true
): UseKycPreferencesResult {
  const [preferences, setPreferences] = useState<KycPreferences | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchPreferences = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const result = await client.getPreferences();
      setPreferences(result);
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to fetch KYC preferences');
      setError(error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, [client]);

  const updatePreferences = useCallback(
    async (update: UpdateKycPreferencesRequest) => {
      setLoading(true);
      setError(null);

      try {
        const result = await client.updatePreferences(update);
        setPreferences(result);
      } catch (err) {
        const error = err instanceof Error ? err : new Error('Failed to update KYC preferences');
        setError(error);
        throw error;
      } finally {
        setLoading(false);
      }
    },
    [client]
  );

  useAutoFetch(autoFetch, fetchPreferences);

  return {
    preferences,
    loading,
    error,
    updatePreferences,
    refresh: fetchPreferences,
  };
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Convert a File to base64 string for document upload
 *
 * @param file - File object from input or drag-drop
 * @returns Base64 encoded string
 *
 * @example
 * ```typescript
 * const handleFileSelect = async (e: ChangeEvent<HTMLInputElement>) => {
 *   const file = e.target.files?.[0];
 *   if (file) {
 *     const base64 = await fileToBase64(file);
 *     setDocumentProof(base64);
 *   }
 * };
 * ```
 */
export function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      // Remove data URL prefix (e.g., "data:image/png;base64,")
      const base64 = result.split(',')[1];
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

/**
 * Validate file type for document upload
 *
 * @param file - File object to validate
 * @param allowedTypes - Array of allowed MIME types
 * @returns boolean indicating if file type is valid
 *
 * @example
 * ```typescript
 * const validTypes = ['image/jpeg', 'image/png', 'application/pdf'];
 * if (!isValidFileType(file, validTypes)) {
 *   alert('Invalid file type. Please upload a JPG, PNG, or PDF.');
 * }
 * ```
 */
export function isValidFileType(
  file: File,
  allowedTypes: string[] = ['image/jpeg', 'image/png', 'image/webp', 'application/pdf']
): boolean {
  return allowedTypes.includes(file.type);
}

/**
 * Validate file size for document upload
 *
 * @param file - File object to validate
 * @param maxSizeBytes - Maximum file size in bytes (default: 10MB)
 * @returns boolean indicating if file size is valid
 *
 * @example
 * ```typescript
 * const maxSize = 5 * 1024 * 1024; // 5MB
 * if (!isValidFileSize(file, maxSize)) {
 *   alert('File too large. Maximum size is 5MB.');
 * }
 * ```
 */
export function isValidFileSize(file: File, maxSizeBytes: number = 10 * 1024 * 1024): boolean {
  return file.size <= maxSizeBytes;
}

/**
 * Format KYC status for display
 *
 * @param status - KYC status
 * @returns Human-readable status string
 */
export function formatKycStatus(status: string): string {
  const statusMap: Record<string, string> = {
    pending: 'Pending Review',
    accepted: 'Approved',
    declined: 'Rejected',
    'review.pending': 'Under Review',
    unknown: 'Unknown',
  };
  return statusMap[status] || status;
}

/**
 * Get status color for UI display
 *
 * @param status - KYC status
 * @returns CSS color class or hex color
 */
export function getStatusColor(status: string): string {
  const colorMap: Record<string, string> = {
    pending: '#f59e0b', // amber
    accepted: '#10b981', // green
    declined: '#ef4444', // red
    'review.pending': '#3b82f6', // blue
    unknown: '#6b7280', // gray
  };
  return colorMap[status] || '#6b7280';
}

/**
 * Get risk level color for UI display
 *
 * @param risk - Risk level
 * @returns CSS color class or hex color
 */
export function getRiskColor(risk: string): string {
  const colorMap: Record<string, string> = {
    low: '#10b981', // green
    medium: '#f59e0b', // amber
    high: '#f97316', // orange
    critical: '#ef4444', // red
  };
  return colorMap[risk] || '#6b7280';
}



export function useReuseKYCSubmission(client: KycClient) {
  const verifyFace = (reference: string, token: string) => {
    return new Promise<CheckKycStatusResponse | null>((resolve, reject) => {
      const container = document.createElement("div")
      document.body.appendChild(container)
      const root = createRoot(container)

      const cleanup = () => {
        root.unmount()
        document.body.removeChild(container)
      }

      const modal = createElement(FaceCaptureModal, {
        onCapture: async (base64: string) => {
          try {
            const resp = await client.submitReuseKycSession({
              proof: base64,
              reference,
              token,
            })
            resolve(resp)
          } catch (error) {
            reject(error instanceof Error ? error : new Error('Face verification failed'))
          }
          finally {
            cleanup()
          }
        },
        onCancel: () => {
          cleanup()
          resolve(null)
        },
      })
      root.render(modal)
    })
  }

  return { verifyFace }
}
